import React, { useEffect, useRef, useState } from 'react';
import DatePicker, { DateObject, DatePickerRef } from 'react-multi-date-picker';
import 'react-multi-date-picker/styles/colors/red.css';
import Icon from 'react-multi-date-picker/components/icon';
import { dayjs, FORMAT_DATE_API } from '@umanforge/common';
import { useTranslation } from 'react-i18next';
import { DateLocaleService } from 'services/dateLocale.service';
import { Button } from './button.component';


interface DatePickerSoloInputProp {
  placeholder?: string;
  disable?: boolean;
  width?: string;
  minDate?: dayjs.Dayjs;
  maxDate?: dayjs.Dayjs;
  defaultValue?: dayjs.Dayjs | null;
  clearAfrerClose?: boolean;
  isWeek?: boolean;
  onChange?: ( date: dayjs.Dayjs ) => void;
  onClose?: ( date: dayjs.Dayjs ) => void;
  className?: string;
  dateFormat?: string;
}

const DatePickerSoloInput: React.FC<DatePickerSoloInputProp> = ( {
  placeholder,
  disable,
  width = 'w-full',
  minDate,
  maxDate,
  defaultValue,
  clearAfrerClose,
  isWeek = false,
  onChange,
  onClose,
  className = '',
  dateFormat = `${ isWeek ? '' : 'dddd' } LL`
} ) => {
  const { t } = useTranslation();
  const pickerRef = useRef();

  const [ date, setDate ] = useState<dayjs.Dayjs | null>( defaultValue ?? null );
  const [ isOpen, setIsOpen ] = useState( false );
  const localDayjs = dayjs.Ls[ dayjs.locale() ];

  useEffect( () => {
    if ( !isOpen ) {
      if ( onClose && date ) {
        onClose( date );
      }
      if ( clearAfrerClose ) {
        setDate( null );
      }
    }
  }, [ isOpen ] );

  useEffect( () => {
    setDate( defaultValue ?? null );
  }, [ defaultValue ] );

  const handleChange = ( dateObjects: DateObject | dayjs.Dayjs ) => {
    setDate( dayjs( dateObjects.format( FORMAT_DATE_API ) ) );

    if ( onChange ) {
      onChange( dayjs( dateObjects.format( FORMAT_DATE_API ) ) );
    }
  };

  const openCalendar = () => {
    const ref = ( pickerRef.current as unknown as DatePickerRef | null );
    if ( ref ) {
      if ( !isOpen ) {
        ref.openCalendar();
      }
    }
  };

  function CustomInput( { onFocus, onChange_ }: { onFocus: React.FocusEventHandler<HTMLInputElement> | undefined; value: string | number | readonly string[] | undefined; onChange_: React.ChangeEventHandler<HTMLInputElement> | undefined; } ) {
    const dateString = DateLocaleService.displayDate( date, dateFormat );
    return (
      <input
        name='rmdp-input-date'
        className='rmdp-input'
        onFocus={onFocus}
        value={ isWeek ? `${ t( 'translation:week_of' )} ${dateString}` : dateString}
        onChange={onChange_}
        readOnly={true}
      />
    );
  }

  function TodayButton( { }: { position: string } ) {
    return <>
      <Button
        onClick={ () => { handleChange( dayjs() ); }}
        label={ t( 'today' ) }
        color='#0074d9'
        padding={0}
      />
    </>;
  }

  return (
    <div className={`flex h-full ${width} `}>
      {/* Input */}
      <div className={
        'flex h-full w-full m-0 py-1 px-2 items-center ' +
        ( isOpen ? ' border-gray-300 ' : ' ' ) +
        className
      } onClick={ openCalendar }>

        <DatePicker
          disabled={disable}
          zIndex={1000}
          minDate={minDate?.format()}
          maxDate={maxDate?.format()}
          placeholder={ placeholder }
          value={date?.toDate()}
          onChange={ dateObj => { if ( dateObj ) { handleChange( dateObj ); } } }
          onOpen={ () => { setIsOpen( true ); } }
          onClose={ () => { setIsOpen( false ); } }
          showOtherDays
          ref={pickerRef}
          render={ <CustomInput onFocus={undefined} value={undefined} onChange_={undefined}/> }
          weekStartDayIndex={ localDayjs.weekStart }
          weekDays={ localDayjs.weekdaysShort }
          months={ localDayjs.months }
          format={ localDayjs.formats.L }
          plugins={[ <TodayButton key={'today_button'} position='bottom'/> ]}
        />
        <div className='h-full px-2 cursor-pointer' onClick={ openCalendar }>
          <Icon className='h-full p-0.5'/>
        </div>
      </div>
    </div>
  );
};

export default DatePickerSoloInput;
